import React, { useState, useRef, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LocationSwitcher from '../Common/LocationSwitcher';
import NotificationBell from '../Common/NotificationBell';
import { useHRMPendingCounts } from '../HRM/hooks/useHRMPendingCounts';
import { getSlimLogoPath, isProductionAppEnv } from '../../utils/slimLogo';
import SecureImage from '../Common/SecureImage';
import { hasStdEmployeeRole, getStdEmployeeNavigation } from './stdEmployeeNavigation';
import { hasHodRole, getHodEmployeeNavigation } from './hodEmployeeNavigation';
import { TOP_DASHBOARD_PERMISSION } from './topDashboardAccess';


// Simple error boundary component
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    console.error('MainLayout Error:', error, errorInfo);

    // Auto-reload if a dynamic import (lazy chunk) fails due to a new deployment
    const isDynamicImportError =
      error?.message?.includes('Failed to fetch dynamically imported module') ||
      error?.message?.includes('Importing a module script failed') ||
      error?.name === 'ChunkLoadError';

    if (isDynamicImportError) {
      const lastReload = Number(sessionStorage.getItem('vite_preload_reload') || '0');
      const now = Date.now();
      if (now - lastReload > 10000) {
        sessionStorage.setItem('vite_preload_reload', String(now));
        window.location.reload();
      }
    }
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Something went wrong</h2>
            <p className="text-gray-600 mb-4">Please refresh the page to try again.</p>
            <button
              onClick={() => window.location.reload()}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              Refresh Page
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const MainLayout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [openBreadcrumbHelp, setOpenBreadcrumbHelp] = useState(false);

  // All known navigation groups — keep this in sync with navigationGroups names below
  const ALL_NAV_GROUPS = ['Overview', 'HRM', 'Finance', 'Inventory', 'Administration'];

  // Load expanded groups from localStorage or use default
  const getInitialExpandedGroups = () => {
    try {
      const saved = localStorage.getItem('sidebar-expanded-groups');
      if (saved) {
        const parsed = JSON.parse(saved);
        // Migration: auto-expand any new groups that weren't in the saved state
        const newGroups = ALL_NAV_GROUPS.filter(g => !parsed.includes(g));
        if (newGroups.length > 0) {
          const merged = [...parsed, ...newGroups];
          localStorage.setItem('sidebar-expanded-groups', JSON.stringify(merged));
          return new Set(merged);
        }
        return new Set(parsed);
      }
    } catch (error) {
      console.error('Error loading sidebar state:', error);
    }
    // Default: all groups expanded
    return new Set(ALL_NAV_GROUPS);
  };

  const [expandedGroups, setExpandedGroups] = useState(getInitialExpandedGroups);
  const [expandedSubmenus, setExpandedSubmenus] = useState(new Set(['Account Payable', 'General Ledger', 'Education', 'Organization', 'Reports', 'Shift Roster', 'Report', 'Approve'])); // Default expand Account Payable, General Ledger, Education, Organization, Reports, Shift Roster and Approve submenus
  const SLIM_HIDE = isProductionAppEnv();
  const { user, logout, hasPermission, isAdmin } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const dropdownRef = useRef(null);
  const navScrollRef = useRef(null);
  const isHrmRoute = location.pathname.startsWith('/hrm')
    && !location.pathname.startsWith('/hrm/reports/audit-logs');
  const shouldFetchHrmCounts = expandedGroups.has('HRM') && isHrmRoute;
  const hrmPendingCounts = useHRMPendingCounts(hasPermission, user, shouldFetchHrmCounts);

  // Toggle group expansion and save to localStorage
  const toggleGroup = (groupName) => {
    setExpandedGroups(prev => {
      const newSet = new Set(prev);
      if (newSet.has(groupName)) {
        newSet.delete(groupName);
      } else {
        newSet.add(groupName);
      }

      // Save to localStorage
      try {
        localStorage.setItem('sidebar-expanded-groups', JSON.stringify([...newSet]));
      } catch (error) {
        console.error('Error saving sidebar state:', error);
      }

      return newSet;
    });
  };

  // Toggle submenu expansion
  const toggleSubmenu = (submenuName) => {
    setExpandedSubmenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(submenuName)) {
        newSet.delete(submenuName);
      } else {
        newSet.add(submenuName);
      }
      return newSet;
    });
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }

      if (!event.target.closest('[data-breadcrumb-help]')) {
        setOpenBreadcrumbHelp(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  useEffect(() => {
    setOpenBreadcrumbHelp(false);
  }, [location.pathname]);

  // Collapse all sidebar groups and submenus when on the dashboard
  useEffect(() => {
    if (location.pathname === '/dashboard') {
      const isRestrictedRole = hasStdEmployeeRole(user) || hasHodRole(user);
      const canViewTopDashboard = hasPermission(TOP_DASHBOARD_PERMISSION);
      // Restricted roles are sent to HRM My Dashboard unless they have SYSTEM:DASHBOARD:READ
      if (isRestrictedRole && !canViewTopDashboard) {
        navigate('/hrm/dashboard', { replace: true });
        return;
      }
      const collapsed = new Set();
      setExpandedGroups(collapsed);
      setExpandedSubmenus(new Set());
      try {
        localStorage.setItem('sidebar-expanded-groups', JSON.stringify([]));
      } catch (error) {
        console.error('Error saving sidebar state:', error);
      }
    }
  }, [location.pathname, user]);

  // On a full page reload the sidebar re-mounts and jumps to the top, which is
  // annoying when the active page lives far down the (long) navigation list.
  // Scroll the active nav item into the center of the sidebar once the
  // auto-expand effects have settled. Retries across a few animation frames
  // because expansion happens in a follow-up render that changes layout height.
  useEffect(() => {
    const container = navScrollRef.current;
    if (!container) return;

    let attempts = 0;
    let rafId = null;

    const centerActiveItem = () => {
      const el = container.querySelector('[data-nav-active="true"]');
      if (el) {
        const containerRect = container.getBoundingClientRect();
        const elRect = el.getBoundingClientRect();
        const delta =
          (elRect.top - containerRect.top) -
          (container.clientHeight / 2 - el.clientHeight / 2);
        // Scroll only the sidebar container (not the window).
        container.scrollTop += delta;
        return;
      }
      // Active item not rendered yet (group/submenu still collapsing/expanding).
      if (attempts++ < 20) {
        rafId = requestAnimationFrame(centerActiveItem);
      }
    };

    rafId = requestAnimationFrame(centerActiveItem);
    return () => {
      if (rafId) cancelAnimationFrame(rafId);
    };
    // Mount only — restores position after a full reload, not on SPA navigation.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Auto-expand active menu groups and submenus on path change
  useEffect(() => {
    if (location.pathname === '/dashboard') return;

    let groupsToExpand = new Set(expandedGroups);
    let submenusToExpand = new Set(expandedSubmenus);
    let updated = false;

    for (const group of navigationGroups) {
      for (const item of group.items) {
        if (item.subItems) {
          for (const subItem of item.subItems) {
            if (subItem.isSubSubmenu && subItem.subItems) {
              for (const subSubItem of subItem.subItems) {
                if (location.pathname === subSubItem.href || location.pathname.startsWith(subSubItem.href + '/')) {
                  if (!groupsToExpand.has(group.name)) {
                    groupsToExpand.add(group.name);
                    updated = true;
                  }
                  if (!submenusToExpand.has(item.name)) {
                    submenusToExpand.add(item.name);
                    updated = true;
                  }
                  if (!submenusToExpand.has(subItem.name)) {
                    submenusToExpand.add(subItem.name);
                    updated = true;
                  }
                }
              }
            } else {
              if (subItem.href && (location.pathname === subItem.href || location.pathname.startsWith(subItem.href + '/'))) {
                if (!groupsToExpand.has(group.name)) {
                  groupsToExpand.add(group.name);
                  updated = true;
                }
                if (!submenusToExpand.has(item.name)) {
                  submenusToExpand.add(item.name);
                  updated = true;
                }
              }
            }
          }
        } else {
          if (item.href && (location.pathname === item.href || location.pathname.startsWith(item.href + '/'))) {
            if (!groupsToExpand.has(group.name)) {
              groupsToExpand.add(group.name);
              updated = true;
            }
          }
        }
      }
    }

    if (updated) {
      setExpandedGroups(groupsToExpand);
      setExpandedSubmenus(submenusToExpand);
      try {
        localStorage.setItem('sidebar-expanded-groups', JSON.stringify([...groupsToExpand]));
      } catch (e) {
        console.error(e);
      }
    }
  }, [location.pathname]);

  const navigationGroups = [
    {
      name: 'Overview',
      items: [
        {
          name: 'Dashboard',
          href: '/dashboard',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5a2 2 0 012-2h4a2 2 0 012 2v2H8V5z" />
            </svg>
          ),
        },
      ]
    },
    {
      name: 'Finance',
      items: [
        {
          name: 'Finance Dashboard',
          href: '/finance/dashboard',
          requiredPermission: 'FINANCE:DASHBOARD:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
        },
        {
          name: 'Account Categories',
          href: '/finance/categories',
          requiredPermission: 'FINANCE:ACCOUNT_CATEGORIES:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          ),
        },
        {
          name: 'Control Accounts',
          href: '/finance/control-accounts',
          requiredPermission: 'FINANCE:CONTROL_ACCOUNTS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          ),
        },
        {
          name: 'Banks',
          href: '/finance/banks',
          requiredPermission: 'FINANCE:BANKS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m4 0v-5a1 1 0 011-1h4a1 1 0 011 1v5m-4-5h4" />
            </svg>
          ),
        },
        {
          name: 'Bank Branches',
          href: '/finance/branches',
          requiredPermission: 'FINANCE:BRANCHES:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m4 0v-5a1 1 0 011-1h4a1 1 0 011 1v5m-4-5h4" />
            </svg>
          ),
        },
        {
          name: 'Chart of Accounts',
          href: '/finance/chart-of-accounts',
          requiredPermission: 'FINANCE:CHART_OF_ACCOUNTS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          ),
        },
        // {
        //   name: 'Bulk Import',
        //   href: '/finance/bulk-import',
        //   requiredPermission: 'FINANCE:CHART_OF_ACCOUNTS:CREATE',
        //   icon: (
        //     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        //     </svg>
        //   ),
        // },
        // {
        //   name: 'Opening Balance Import',
        //   href: '/finance/opening-balance-import',
        //   icon: (
        //     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-8l-4-4m0 0L8 8m4-4v12" />
        //     </svg>
        //   ),
        // },
        {
          name: 'Cost Centers',
          href: '/finance/cost-centers',
          requiredPermission: 'FINANCE:COST_CENTERS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h1a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ),
        },
        {
          name: 'Budget Management',
          href: '/finance/budget',
          requiredPermission: 'FINANCE:BUDGET:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          ),
        },
        {
          name: 'Petty Cash Categories',
          href: '/finance/petty-cash-categories',
          requiredPermission: 'FINANCE:PETTY_CASH_CATEGORIES:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          ),
        },


        {
          name: 'Transactions',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Bill Entry',
              href: '/finance/bill-entry',
              requiredPermission: 'FINANCE:BILL_ENTRY:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Bill Payments',
              href: '/finance/bill-payment',
              requiredPermission: 'FINANCE:BILL_PAYMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              ),
            },
            {
              name: 'Normal Payment',
              href: '/finance/normal-payment',
              requiredPermission: 'FINANCE:NORMAL_PAYMENTS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                </svg>
              ),
            },
            {
              name: 'Journal Entry',
              href: '/finance/journal-entry',
              requiredPermission: 'FINANCE:JOURNAL_ENTRIES:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Petty Cash Payment',
              href: '/finance/petty-cash-payment',
              requiredPermission: 'FINANCE:PETTY_CASH_PAYMENTS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
            {
              name: 'Petty Cash Reimbursement',
              href: '/finance/petty-cash-reimbursement',
              requiredPermission: 'FINANCE:PETTY_CASH_REIMBURSEMENTS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                </svg>
              ),
            },
            {
              name: 'Fund Transfer',
              href: '/finance/fund-transfer',
              requiredPermission: 'FINANCE:FUND_TRANSFERS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              ),
            },
            {
              name: 'Bank Deposit',
              href: '/finance/bank-deposit',
              requiredPermission: 'FINANCE:BANK_DEPOSITS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-4m-5 0H9m0 0H5m4 0v-5a1 1 0 011-1h4a1 1 0 011 1v5m-4-5h4" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v4m0 0l-2-2m2 2l2-2" />
                </svg>
              ),
            },
            {
              name: 'Bank Reconciliation',
              href: '/finance/bank-reconciliation',
              requiredPermission: 'FINANCE:BANK_RECONCILIATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Reports',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Trial Balance',
              href: '/finance/trial-balance',
              requiredPermission: 'FINANCE:REPORT_TRIAL_BALANCE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'General Ledger',
              href: '/finance/reports/general-ledger',
              requiredPermission: 'FINANCE:REPORT_GENERAL_LEDGER:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Ledger Detail',
              href: '/finance/reports/ledger-detail',
              requiredPermission: 'FINANCE:REPORT_LEDGER_DETAIL:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              ),
            },
            {
              name: 'Student Statement',
              href: '/finance/reports/student-statement',
              requiredPermission: 'FINANCE:REPORT_STUDENT_STATEMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ),
            },
            {
              name: 'External Company Statement',
              href: '/finance/reports/external-company-statement',
              requiredPermission: 'FINANCE:REPORT_EXTERNAL_COMPANY_STATEMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                </svg>
              ),
            },
            {
              name: 'T&W Transactions',
              href: '/finance/reports/tw-transactions',
              requiredPermission: 'FINANCE:REPORT_TW_TRANSACTIONS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              ),
            },
            {
              name: 'Income Statement',
              href: '/finance/reports/income-statement',
              requiredPermission: 'FINANCE:REPORT_INCOME_STATEMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ),
            },
            {
              name: 'Module-Wise Revenue',
              href: '/finance/reports/module-revenue',
              requiredPermission: 'FINANCE:REPORT_MODULE_REVENUE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z" />
                </svg>
              ),
            },
            {
              name: 'Balance Sheet',
              href: '/finance/reports/balance-sheet',
              requiredPermission: 'FINANCE:REPORT_BALANCE_SHEET:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3" />
                </svg>
              ),
            },
            {
              name: 'Cash Flow',
              href: '/finance/reports/cash-flow',
              requiredPermission: 'FINANCE:REPORT_CASH_FLOW:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              ),
            },
            {
              name: 'Budget Report',
              href: '/finance/reports/budget',
              requiredPermission: 'FINANCE:REPORT_BUDGET:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
            {
              name: 'Accounts Payable',
              href: '/finance/reports/accounts-payable',
              requiredPermission: 'FINANCE:REPORT_AP_AGING:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3v-6m-3 6v-1m-5 4h16a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v13a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'Accounts Receivable',
              href: '/finance/reports/accounts-receivable',
              requiredPermission: 'FINANCE:REPORT_AR_AGING:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Asset Management',
          isSubmenu: true,
          requiredPermission: 'FINANCE:ASSET_SETTINGS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
            </svg>
          ),
          subItems: [
            {
              name: 'Settings',
              href: '/finance/asset-settings',
              requiredPermission: 'FINANCE:ASSET_SETTINGS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ),
            },
            {
              name: 'Groups',
              href: '/finance/asset-groups',
              requiredPermission: 'FINANCE:ASSET_GROUPS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                </svg>
              ),
            },
            {
              name: 'Types',
              href: '/finance/asset-types',
              requiredPermission: 'FINANCE:ASSET_TYPES:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              ),
            },
            {
              name: 'Categories',
              href: '/finance/asset-categories',
              requiredPermission: 'FINANCE:ASSET_CATEGORIES:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
                </svg>
              ),
            },
            {
              name: 'Brands',
              href: '/finance/asset-brands',
              requiredPermission: 'FINANCE:ASSET_BRANDS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              ),
            },
            {
              name: 'Models',
              href: '/finance/asset-models',
              requiredPermission: 'FINANCE:ASSET_MODELS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 3H5a2 2 0 00-2 2v4m6-6h10a2 2 0 012 2v4M9 3v18m0 0h10a2 2 0 002-2V9M9 21H5a2 2 0 01-2-2V9m0 0h18" />
                </svg>
              ),
            },
            {
              name: 'Assets',
              href: '/finance/assets',
              requiredPermission: 'FINANCE:ASSETS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              ),
            },
            {
              name: 'Asset Allocations',
              href: '/finance/asset-allocations',
              requiredPermission: 'FINANCE:ASSET_ALLOCATIONS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ),
            },
            {
              name: 'Asset Depreciation',
              href: '/finance/asset-depreciation',
              requiredPermission: 'FINANCE:ASSET_DEPRECIATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 17h8m0 0V9m0 8l-8-8-4 4-6-6" />
                </svg>
              ),
            },
            {
              name: 'Reports',
              href: '/finance/asset-reports',
              requiredPermission: 'FINANCE:ASSET_REPORTS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Finance Settings',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
          subItems: [
            {
              name: 'CRM Payment Settings',
              href: '/finance/payment-settings',
              requiredPermission: 'FINANCE:PAYMENT_SETTINGS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                </svg>
              ),
            },
            {
              name: 'Cheque Print Layouts',
              href: '/finance/cheque-print-settings',
              requiredPermission: 'FINANCE:CHEQUE_PRINT_SETTINGS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2H5a2 2 0 01-2-2V8z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 15h4M14 15h4" />
                </svg>
              ),
            },
            {
              name: 'Transaction Ledger Mapping',
              href: '/finance/transaction-ledger-mapping',
              requiredPermission: 'FINANCE:TRANSACTION_SETTINGS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              ),
            },
            {
              name: 'Tax Invoice Numbering',
              href: '/finance/tax-invoice-numbers',
              requiredRole: 'SUPER_ADMIN',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h10M7 12h10M7 17h6M5 4h14a2 2 0 012 2v14l-3-2-3 2-3-2-3 2V6a2 2 0 012-2z" />
                </svg>
              ),
            },
            {
              name: 'General Settings',
              href: '/finance/general-settings',
              requiredRole: 'SUPER_ADMIN',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              ),
            },
            {
              name: 'Tax Configurations',
              href: '/finance/tax-configs',
              requiredPermission: 'FINANCE:TAX_CONFIGS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                </svg>
              ),
            },
          ]
        }
      ]
    },
    {
      name: 'Hire Purchase',
      items: [
        {
          name: 'HP Dashboard',
          href: '/hire-purchase/dashboard',
          requiredPermission: 'HIRE_PURCHASE:DASHBOARD:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
          ),
        },
        {
          name: 'Customers & Guarantors',
          href: '/hire-purchase/persons',
          requiredPermission: 'HIRE_PURCHASE:PERSON:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ),
        },
        {
          name: 'Vehicle Catalogue',
          href: '/hire-purchase/vehicle-models',
          requiredPermission: 'HIRE_PURCHASE:BIKE:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h10M4 18h10" />
            </svg>
          ),
        },
        {
          name: 'Bike Registry',
          href: '/hire-purchase/bikes',
          requiredPermission: 'HIRE_PURCHASE:BIKE:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 17a2 2 0 104 0 2 2 0 00-4 0zm10 0a2 2 0 104 0 2 2 0 00-4 0zM7 17h8m-8 0l2-7h6l2 7M9 10V7h4l2 3" />
            </svg>
          ),
        },
        {
          name: 'HP Applications',
          href: '/hire-purchase/applications',
          requiredPermission: 'HIRE_PURCHASE:APPLICATION:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
        },
        {
          name: 'HP Quotation Calculator',
          href: '/hire-purchase/calculator',
          requiredPermission: 'HIRE_PURCHASE:APPLICATION:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
            </svg>
          ),
        },
        {
          name: 'HP Short-Term Loans',
          href: '/hire-purchase/short-term-loan',
          requiredPermission: 'HIRE_PURCHASE:APPLICATION:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        },
        {
          name: 'HP Rate Sheet',
          href: '/hire-purchase/rate-sheet',
          requiredPermission: 'HIRE_PURCHASE:APPLICATION:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18m-9-4v8m-7 0h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
            </svg>
          ),
        },
        {
          name: 'HP Facilities',
          href: '/hire-purchase/facilities',
          requiredPermission: 'HIRE_PURCHASE:FACILITY:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h1a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ),
        },
        {
          name: 'HP Receipts',
          href: '/hire-purchase/receipts',
          requiredPermission: 'HIRE_PURCHASE:RECEIPT:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
            </svg>
          ),
        },
        {
          name: 'Penalties',
          href: '/hire-purchase/penalties',
          requiredPermission: 'HIRE_PURCHASE:PENALTY:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01M5.07 19H18.93a2 2 0 001.72-3L13.72 4a2 2 0 00-3.44 0L3.35 16a2 2 0 001.72 3z" />
            </svg>
          ),
        },
        {
          name: 'Settlements',
          href: '/hire-purchase/settlements',
          requiredPermission: 'HIRE_PURCHASE:SETTLEMENT:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
        },
        {
          name: 'HP Reports',
          href: '/hire-purchase/reports/arrears',
          requiredPermission: 'HIRE_PURCHASE:REPORT_ARREARS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
        },
        {
          name: 'HP Charges & Insurance',
          href: '/hire-purchase/charges',
          requiredPermission: 'HIRE_PURCHASE:SETTINGS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
            </svg>
          ),
        },
        {
          name: 'HP Settings',
          href: '/hire-purchase/settings',
          requiredPermission: 'HIRE_PURCHASE:SETTINGS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
        },
      ]
    },
    {
      name: 'HRM',
      items: [
        {
          name: 'Admin Dashboard',
          href: '/hrm/admin-dashboard',
          requiredPermission: 'HRM:ADMIN_HRM_DASHBOARD:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
        },
        {
          name: 'My Dashboard',
          href: '/hrm/dashboard',
          requiredPermission: 'HRM:EMP_DASHBOARD:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ),
        },
        {
          name: 'Employee Self-Service',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 16c2.5 0 4.847.655 6.879 1.804M15 10a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
          ),
          subItems: [
            {
              name: 'My Dashboard',
              href: '/hrm/dashboard',
              requiredPermission: 'HRM:EMP_DASHBOARD:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
            {
              name: 'Leave Requests',
              href: '/hrm/ess/leave',
              requiredPermission: 'HRM:EMP_LEAVE_APPLICATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'My Attendance',
              href: '/hrm/ess/attendance',
              requiredPermission: 'HRM:EMP_ATTENDANCE:SHOW',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'My Cancel Requests',
              href: '/hrm/leave/self-cancel-requests',
              requiredPermission: 'HRM:EMP_LEAVE_CANCEL:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ),
            },
            {
              name: 'My Work Calendar',
              href: '/hrm/work-calendar',
              requiredPermission: 'HRM:EMP_WORK_CALENDAR:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
            },
            ...(
              SLIM_HIDE
                ? []
                : [
                  {
                    name: 'Expense Claims',
                    href: '/hrm/ess/expenses',
                    requiredPermission: 'HRM:EMP_EXPENSES:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                      </svg>
                    ),
                  }
                ]
            ),
            {
              name: 'HR Tickets',
              href: '/hrm/ess/tickets',
              requiredPermission: 'HRM:EMP_TICKETS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              ),
            },

            ...(
              SLIM_HIDE
                ? []
                : [
                  {
                    name: 'Resignation',
                    href: '/hrm/ess/resignation',
                    requiredPermission: 'HRM:EMP_RESIGNATION:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                    ),
                  }
                ]
            ),

            ...(
              SLIM_HIDE
                ? []
                : [
                  {
                    name: 'My Roster',
                    href: '/hrm/shift-roster/my-roster',
                    requiredPermission: 'HRM:EMP_ROSTER:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    ),
                  }
                ]
            ),
            ...(
              SLIM_HIDE
                ? []
                : [
                  {
                    name: 'Request Shift Swaps',
                    href: '/hrm/shift-roster/request-shift-swaps',
                    requiredPermission: 'HRM:ROSTER_ASSIGNMENT:REQUEST_SWAP',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    ),
                  }
                ]
            ),
            {
              name: 'My Performance',
              href: '/hrm/performance-management/self',
              requiredPermission: 'HRM:EMP_PERFORMANCE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ),
            },
          ]
        },


        {
          name: 'Employee Management',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Employees',
              href: '/hrm/employees',
              requiredPermission: 'HRM:EMPLOYEE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197M13 7a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              ),
            },
            { name: 'Employee Life Cycle', isHeader: true },
            {
              name: 'Dashboard',
              href: '/hrm/employee-lifecycle/dashboard',
              requiredPermission: 'HRM:EMPLOYEE_LIFECYCLE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
            {
              name: 'Onboarding',
              href: '/hrm/employee-lifecycle/onboarding',
              requiredPermission: 'HRM:ONBOARDING:READ',
              badge: hrmPendingCounts.onboarding,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z" />
                </svg>
              ),
            },
            {
              name: 'Confirmations',
              href: '/hrm/employee-lifecycle/confirmations',
              requiredPermission: 'HRM:CONFIRMATION:READ',
              badge: hrmPendingCounts.confirmations,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'Job Changes',
              href: '/hrm/employee-lifecycle/job-changes',
              requiredPermission: 'HRM:JOB_CHANGE:READ',
              badge: hrmPendingCounts.jobChanges,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ),
            },
            {
              name: 'Exit Applications',
              href: '/hrm/employee-lifecycle/separations',
              requiredPermission: 'HRM:SEPARATION:READ',
              badge: hrmPendingCounts.separations,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                </svg>
              ),
            },
            {
              name: 'Life Cycle History',
              href: '/hrm/employee-lifecycle/history',
              requiredPermission: 'HRM:EMPLOYEE_LIFECYCLE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Approve',
          isSubmenu: true,
          badge: (hrmPendingCounts.supervisorLeaveRequests || 0) +
            (hrmPendingCounts.supervisorLeaveCancellations || 0) +
            (hrmPendingCounts.hodLeaveRequests || 0) +
            (hrmPendingCounts.hodLeaveCancellations || 0) +
            (hrmPendingCounts.supervisorAttendance || 0) +
            (hrmPendingCounts.hodAttendance || 0) +
            (hrmPendingCounts.otApprovals || 0) +
            (hrmPendingCounts.requisitions || 0) +
            (hrmPendingCounts.loans || 0),
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          subItems: [
            {
              name: 'HOD Approve Leave',
              href: '/hrm/leave/approve',
              requiredPermission: 'HRM:HOD_LEAVE_REQUEST:READ',
              badge: hrmPendingCounts.supervisorLeaveRequests,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HOD Leave Cancel',
              href: '/hrm/leave/approve-cancellations',
              requiredPermission: 'HRM:HOD_LEAVE_CANCEL:READ',
              badge: hrmPendingCounts.supervisorLeaveCancellations,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HR Leave Req. Approve',
              href: '/hrm/leave/hod-requests',
              requiredPermission: 'HRM:HR_LEAVE_REQUESTS:SHOW',
              badge: hrmPendingCounts.hodLeaveRequests,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              ),
            },
            {
              name: 'HR Leave Cancel Approve',
              href: '/hrm/leave/hod-cancels',
              requiredPermission: ['HRM:HR_LEAVE_CANCELS:SHOW', 'HRM:HR_LEAVE_CANCELS:READ'],
              badge: hrmPendingCounts.hodLeaveCancellations,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HOD Approve Attendance',
              href: '/hrm/attendance/supervisor-approve',
              requiredPermission: 'HRM:HOD_ATTENDANCE:APPROVE',
              badge: hrmPendingCounts.supervisorAttendance,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HR Approve Attendance',
              href: '/hrm/attendance/hod/approve',
              requiredPermission: 'HRM:HR_ATTENDANCE_REQUESTS:SHOW',
              badge: hrmPendingCounts.hodAttendance,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'OT Approval',
              href: '/hrm/payroll/time/ot-approvals',
              requiredPermission: 'HRM:PAY_OT_APPROVAL:READ',
              badge: hrmPendingCounts.otApprovals,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m8-3a8 8 0 11-16 0 8 8 0 0116 0z" />
                </svg>
              ),
            },
            ...(
              SLIM_HIDE
                ? []
                : [
                  {
                    name: 'Approve Shift Swaps',
                    href: '/hrm/shift-roster/approve-shift-swaps',
                    requiredPermission: 'HRM:ROSTER_ASSIGNMENT:APPROVE_SWAP',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ),
                  }
                ]
            ),
            {
              name: 'Loans & Advances',
              href: '/hrm/payroll/salary/loans-advances',
              requiredPermission: 'HRM:PAYROLL_LOAN:READ',
              badge: hrmPendingCounts.loans,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                </svg>
              ),
            },
            ...(
              SLIM_HIDE
                ? []
                : [
                  {
                    name: 'Job Requisitions',
                    href: '/hrm/recruitment/requisitions',
                    requiredPermission: 'HRM:REQUISITIONS:READ',
                    badge: hrmPendingCounts.requisitions,
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    ),
                  }
                ]
            )
          ]
        },

        {
          name: 'HRM Management',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Ticket Management',
              href: '/hrm/hrm-manage/ticket-manage',
              requiredPermission: 'HRM:HR_TICKETMNG:READ',
              badge: hrmPendingCounts.tickets,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
                </svg>
              ),
            },
            {
              name: 'Email Templates',
              href: '/hrm/hrm-manage/email-templates',
              requiredPermission: 'HRM:EMAIL_TEMPLATE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'Company Documents',
              href: '/hrm/hrm-manage/company-doc',
              requiredPermission: 'HRM:COMPANY_DOCUMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Send Notification',
              href: '/hrm/send-mail',
              description: 'Analyze attendance and send automated warnings or reminders.',
              requiredPermission: 'HRM:HRM_SETTINGS:UPDATE',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'HRM Rules',
              href: '/hrm/leave/rules',
              requiredPermission: 'HRM:HRM_RULES:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            { name: 'Organization', isHeader: true },
            {
              name: 'Departments',
              href: '/hrm/organization/departments',
              requiredPermission: 'HRM:DEPARTMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              ),
            },
            {
              name: 'Divisions',
              href: '/hrm/organization/divisions',
              requiredPermission: 'HRM:DIVISION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" />
                </svg>
              ),
            },
            {
              name: 'Sections',
              href: '/hrm/organization/sections',
              requiredPermission: 'HRM:SECTION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              ),
            },
            {
              name: 'Units',
              href: '/hrm/organization/units',
              requiredPermission: 'HRM:UNIT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                </svg>
              ),
            },
            {
              name: 'Job Families',
              href: '/hrm/organization/job-families',
              requiredPermission: 'HRM:JOB_FAMILY:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'Designations',
              href: '/hrm/organization/positions',
              requiredPermission: 'HRM:DESIGNATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ),
            },
            {
              name: 'Grades',
              href: '/hrm/organization/grades',
              requiredPermission: 'HRM:GRADE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
            {
              name: 'Cost Centers',
              href: '/hrm/organization/cost-centers',
              requiredPermission: 'HRM:COST_CENTER:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'Org Chart',
              href: '/hrm/organization/org-chart',
              requiredPermission: 'HRM:ORG_CHART:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
            {
              name: 'Headcount Planning',
              href: '/hrm/organization/headcount',
              requiredPermission: 'HRM:HEADCOUNT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
          ]
        },
        ...(
          SLIM_HIDE
            ? []
            : [
              {
                name: 'Recruitment',
                isSubmenu: true,
                icon: (
                  <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                ),
                subItems: [
                  {
                    name: 'Dashboard',
                    href: '/hrm/recruitment',
                    requiredPermission: 'HRM:RECRUITMENT:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'Candidates',
                    href: '/hrm/recruitment/candidates',
                    requiredPermission: 'HRM:CANDIDATES:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'Job Requisitions',
                    href: '/hrm/recruitment/requisitions',
                    requiredPermission: 'HRM:REQUISITIONS:READ',
                    badge: hrmPendingCounts.requisitions,
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'Job Postings',
                    href: '/hrm/recruitment/postings',
                    requiredPermission: 'HRM:POSTINGS:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'Interviews',
                    href: '/hrm/recruitment/interviews',
                    requiredPermission: 'HRM:INTERVIEWS:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'Offers',
                    href: '/hrm/recruitment/offers',
                    requiredPermission: 'HRM:OFFERS:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                      </svg>
                    ),
                  },
                ]
              }
            ]
        ),

        {
          name: 'Time & Attendance',
          isSubmenu: true,
          badge: hrmPendingCounts.adminAttendance,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          subItems: [
            { name: 'Shift & Roster', isHeader: true },
            {
              name: 'Shifts',
              href: '/hrm/shift-roster/shifts',
              requiredPermission: 'HRM:SHIFT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            ...(
              SLIM_HIDE
                ? []
                : [
                  {
                    name: 'Approve Shift Swaps',
                    href: '/hrm/shift-roster/approve-shift-swaps',
                    requiredPermission: 'HRM:ROSTER_ASSIGNMENT:APPROVE_SWAP',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                    ),
                  }
                ]
            ),
            {
              name: 'Rosters',
              href: '/hrm/shift-roster/rosters',
              requiredPermission: 'HRM:ROSTER:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              ),
            },
            {
              name: 'Roster Schedules',
              href: '/hrm/shift-roster/schedules',
              requiredPermission: 'HRM:ROSTER_SCHEDULE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'Roster Assignments',
              href: '/hrm/shift-roster/assignments',
              requiredPermission: 'HRM:ROSTER_ASSIGNMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
            ...(
              SLIM_HIDE
                ? []
                : [
                  {
                    name: 'My Roster',
                    href: '/hrm/shift-roster/my-roster',
                    requiredPermission: 'HRM:EMP_ROSTER:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                      </svg>
                    ),
                  },
                  {
                    name: 'Request Shift Swaps',
                    href: '/hrm/shift-roster/request-shift-swaps',
                    requiredPermission: 'HRM:ROSTER_ASSIGNMENT:REQUEST_SWAP',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                    ),
                  },
                  {
                    name: 'Calendar View',
                    href: '/hrm/shift-roster/shift-roster-calendar',
                    requiredPermission: 'HRM:ROSTER_SCHEDULE:READ',
                    icon: (
                      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                      </svg>
                    ),
                  }
                ]
            ),
            { name: 'Attendance Management', isHeader: true },
            {
              name: 'Dashboard',
              href: '/hrm/attendance/dashboard',
              requiredPermission: 'HRM:ATTENDANCE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012 2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
            {
              name: 'Machine Records',
              href: '/hrm/attendance/machine-records',
              requiredPermission: 'HRM:ATTENDANCE_RECORDS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              ),
            },
            {
              name: 'Self Attendance',
              href: '/hrm/attendance/self',
              requiredPermission: 'HRM:EMP_ATTENDANCE:SHOW',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ),
            },
            {
              name: 'Employee Attendance History',
              href: '/hrm/attendance/employee-attendance-history',
              requiredPermission: 'HRM:EMPLOYEE_ATTENDANCE_HISTORY:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HOD Approve Attendance',
              href: '/hrm/attendance/supervisor-approve',
              requiredPermission: 'HRM:HOD_ATTENDANCE:SHOW',
              badge: hrmPendingCounts.supervisorAttendance,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HR Approve Attendance',
              href: '/hrm/attendance/hod/approve',
              requiredPermission: 'HRM:HR_ATTENDANCE_REQUESTS:SHOW',
              badge: hrmPendingCounts.hodAttendance,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HR Manage Attendance',
              href: '/hrm/attendance/manage',
              requiredPermission: 'HRM:HR_ATTENDANCE:MANAGE',
              badge: hrmPendingCounts.adminAttendance,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              ),
            },
            {
              name: 'Attendance Settings',
              href: '/hrm/attendance/settings',
              requiredPermission: 'HRM:HR_ATTENDANCE:SETTINGS',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ),
            },
            { name: 'OT Manage', isHeader: true },
            {
              name: 'OT Groups',
              href: '/hrm/payroll/payroll-groups',
              description: 'Manage OT criteria groups for Cashiers, Drivers, Executives, etc.',
              requiredPermission: 'HRM:OT_GROUP:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
            {
              name: 'OT Approval',
              href: '/hrm/payroll/time/ot-approvals',
              requiredPermission: 'HRM:OT_APPROVAL:READ',
              badge: hrmPendingCounts.otApprovals,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m8-3a8 8 0 11-16 0 8 8 0 0116 0z" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Leave Management',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Leave Types',
              href: '/hrm/leave/types',
              requiredPermission: 'HRM:LEAVE_TYPE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
                </svg>
              ),
            },
            {
              name: 'Entitlements',
              href: '/hrm/leave/entitlements',
              requiredPermission: 'HRM:LEAVE_ENTITLEMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Self Applications',
              href: '/hrm/leave/self-applications',
              requiredPermission: 'HRM:EMP_LEAVE_APPLICATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ),
            },
            {
              name: 'Admin Applications',
              href: '/hrm/leave/applications',
              requiredPermission: 'HRM:ADMIN_LEAVE_APPLICATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'HOD Approve Leave',
              href: '/hrm/leave/approve',
              requiredPermission: 'HRM:HOD_LEAVE_REQUEST:READ',
              badge: hrmPendingCounts.supervisorLeaveRequests,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HOD Leave Cancel',
              href: '/hrm/leave/approve-cancellations',
              requiredPermission: 'HRM:HOD_LEAVE_CANCEL:READ',
              badge: hrmPendingCounts.supervisorLeaveCancellations,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'HR Leave Req. Approve',
              href: '/hrm/leave/hod-requests',
              requiredPermission: 'HRM:HR_LEAVE_REQUESTS:SHOW',
              badge: hrmPendingCounts.hodLeaveRequests,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              ),
            },
            {
              name: 'HR Leave Cancel Approve',
              href: '/hrm/leave/hod-cancels',
              requiredPermission: 'HRM:HR_LEAVE_CANCELS:SHOW',
              badge: hrmPendingCounts.hodLeaveCancellations,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'Leave Balance',
              href: '/hrm/leave/balance',
              requiredPermission: 'HRM:LEAVE_BALANCE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'Encashment',
              href: '/hrm/leave/encashment',
              requiredPermission: 'HRM:LEAVE_ENCASHMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
            {
              name: 'Adjustments',
              href: '/hrm/leave/adjustments',
              requiredPermission: 'HRM:LEAVE_ADJUSTMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                </svg>
              ),
            },
            {
              name: 'Policies',
              href: '/hrm/leave/policies',
              requiredPermission: 'HRM:LEAVE_POLICY:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Employee Calendar',
              href: '/hrm/leave/employee-calendar',
              requiredPermission: 'HRM:EMP_LEAVE_APPLICATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ),
            },
            {
              name: 'Company Calendar',
              href: '/hrm/leave/company-calendar',
              requiredPermission: 'HRM:COMPANY_LEAVE_CALENDAR:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'Holiday Calendar',
              href: '/hrm/leave/holidays',
              requiredPermission: 'HRM:HOLIDAY_CALENDAR:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a2 2 0 112 2h-2zm0 0V5.5A2.5 2.5 0 109.5 8H12zm-7 4h14M5 12a2 2 0 110-4h14a2 2 0 110 4M5 12v7a2 2 0 002 2h10a2 2 0 002-2v-7" />
                </svg>
              ),
            },

            {
              name: 'Reports',
              href: '/hrm/leave/reports',
              requiredPermission: 'HRM:LEAVE_REPORT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
            {
              name: 'My Cancel Requests',
              href: '/hrm/leave/self-cancel-requests',
              requiredPermission: 'HRM:EMP_LEAVE_CANCEL:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              ),
            },
            {
              name: 'HR Cancel Requests',
              href: '/hrm/leave/cancel-requests',
              requiredPermission: ['HRM:HR_LEAVE_CANCELS:SHOW', 'HRM:HR_LEAVE_CANCELS:READ'],
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              ),
            },
            {
              name: 'Leave Settings',
              href: '/hrm/leave/settings',
              requiredPermission: 'HRM:LEAVE_SETTINGS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Performance Management',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'My Performance',
              href: '/hrm/performance-management/self',
              description: 'Allows each employee to view assigned KPIs, current progress, historical scores, and manager feedback in one place. It supports self-assessment and helps employees plan improvements before formal review cycles.',
              requiredPermission: 'HRM:EMP_PERFORMANCE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                </svg>
              ),
            },
            {
              name: 'Performance Dashboard',
              href: '/hrm/performance-management/dashboard',
              description: 'Provides a consolidated performance snapshot across employees, teams, and departments. It highlights KPI completion, pending evaluations, recent score trends, low-performing areas, and high achievers so leadership can take timely action.',
              requiredPermission: 'HRM:PERFORMANCE_DASHBOARD:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
            {
              name: 'KPI Config',
              href: '/hrm/performance-management/kpi-config',
              description: 'Controls KPI target duration, evaluation windows, and other performance-cycle settings used across Performance Management.',
              requiredPermission: 'HRM:KPI_CONFIG:MANAGE',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M7 12h10M10 18h4" />
                </svg>
              ),
            },
            {
              name: 'KPI Overview',
              href: '/hrm/performance-management/overview',
              description: 'Visualizes the organization hierarchy (divisions, heads/supervisors, and employees) alongside average KPI score breakdowns and graphs.',
              requiredPermission: 'HRM:PERFORMANCE_DASHBOARD:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 8v8m-4-5v5m-4-2v2m-2 4h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'HOD Review and Evaluation',
              href: '/hrm/performance-management/hod-evaluations',
              description: 'Lets a Head of Department review and assess the self-evaluations submitted by the team members reporting to them.',
              requiredPermission: 'HRM:HOD_EVALUATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-1.13a4 4 0 10-4-4 4 4 0 004 4zm6 0a4 4 0 10-4-4 4 4 0 004 4z" />
                </svg>
              ),
            },
            {
              name: 'HR KPI Review',
              href: '/hrm/performance-management/hr-kpi-review',
              description: 'Allows HR managers to review, filter, and view the details of all employee KPI evaluations in read-only mode.',
              requiredPermission: 'HRM:HR_KPI:MANAGE',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              ),
            },
            {
              name: 'Rewards & Improvement',
              href: '/hrm/performance-management/rewards',
              description: 'The Rewards and Improvement module uses the results of the performance evaluation process to trigger organizational actions that either reward high performing employees or support employees who require performance improvement.',
              requiredPermission: 'HRM:REWARD_RECOMMENDATION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v13m0-13V6a4 4 0 00-4-4H6a4 4 0 00-4 4v2h4m6 0V6a4 4 0 014-4h2a4 4 0 014 4v2h-4m-6 0h4" />
                </svg>
              ),
            },
            {
              name: 'Performance Reports',
              href: '/hrm/performance-management/reports',
              description: 'The Performance Reports module provides analytical insights into employee performance across the organization. These reports help HR teams and senior management evaluate workforce productivity, identify top performers, and detect performance issues that require intervention.',
              requiredPermission: 'HRM:PERFORMANCE_REPORT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
          ],
        },
        {
          name: 'Payroll Management',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Payroll Admin Dashboard',
              href: '/hrm/payroll/dashboard',
              description: 'Comprehensive HRM intelligence center presenting payroll status feeds, salary budgets, automated run schedules, and compliance metrics.',
              requiredPermission: 'HRM:PAYROLL_ADMIN_DASHBOARD:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h7" />
                </svg>
              ),
            },
            { name: 'Payroll Configuration', isHeader: true },
            {
              name: 'Payroll Configurations',
              href: '/hrm/payroll/configurations',
              description: 'Centralized policy definitions panel. Define, categorize, and control static/dynamic salary components—including Fixed Allowances, Variable Payments (OT, bonuses), Variable Allowances, and recurring deductions.',
              requiredPermission: [
                'HRM:PAYROLL_CONFIG_MANAGE:READ',
                'HRM:PAYROLL_FIXED_ALLOWANCE:READ',
                'HRM:PAYROLL_VARIABLE_PAYMENT:READ',
                'HRM:PAYROLL_VARIABLE_ALLOWANCE:READ',
                'HRM:PAYROLL_OTHER_DEDUCTION:READ'
              ],
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ),
            },
            {
              name: 'Salary Cycle',
              href: '/hrm/payroll/setup/periods',
              description: 'Configure, active, and lock processing periods, track payment cycles, and set up hourly rate schedules for overtime calculations.',
              requiredPermission: [
                'HRM:PAYROLL_CYCLE:READ',
                'HRM:PAYROLL_PERIOD:READ'
              ],
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'Time & Attendance',
              href: '/hrm/payroll/attendance-summary-list',
              description: 'Integration matrix linking daily punch records, active shifts, leave balances, late-in/early-out logs, and calculated overtime stats directly into the active payroll cycle.',
              requiredPermission: 'HRM:PAYROLL_ATTENDANCE:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              ),
            },
            {
              name: 'Employee Salaries',
              href: '/hrm/payroll/employee-salaries',
              description: 'Manage individual employee base salaries, customize compensation plans, and track total cost-to-company (CTC) allocations.',
              requiredPermission: 'HRM:PAYROLL_SALARY:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 25" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h1a1 1 0 011 1v5m-4 0h4" />
                </svg>
              ),
            },

            { name: 'Payments & Deductions', isHeader: true },
            {
              name: 'One Time Payments',
              href: '/hrm/payroll/salary/one-time-payments',
              description: 'Variable compensation engine designed to launch ad-hoc incentives, performance bonuses, festival advances, and expense reimbursements. All transactions mapped under dynamic pay periods undergo strict EPF/ETF statutory validations prior to commit.',
              requiredPermission: 'HRM:PAYROLL_PAYMENT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
            {
              name: 'Loans & Advances',
              href: '/hrm/payroll/salary/loans-advances',
              description: 'Manage employee loans, salary advances, interest schedules, repayment tracks, and processing configurations.',
              requiredPermission: 'HRM:PAYROLL_LOAN:READ',
              badge: hrmPendingCounts.loans,
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 14v3m4-3v3m4-3v3M3 21h18M3 10h18M3 7l9-4 9 4M4 10h16v11H4V10z" />
                </svg>
              ),
            },

            { name: 'Processing & Verification', isHeader: true },
            {
              name: 'Pre-Payroll Review',
              href: '/hrm/payroll/processing/setup',
              description: 'Pre-processing verification panel. Verify base salaries, input dynamic variable hours, apply custom manual overrides, and review payment components prior to payroll execution.',
              requiredPermission: 'HRM:PAYROLL_PROCESS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
              ),
            },
            {
              name: 'HR Verify',
              href: '/hrm/payroll/hr-verify',
              description: 'Initialize salary matrix, verify individual employee calculations, execute batch payroll calculations, and review processing runs.',
              requiredPermission: 'HRM:PAYROLL_PROCESS:CREATE',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            { name: 'Payroll Approvals', isHeader: true },
            {
              name: 'HR Approval',
              href: '/hrm/payroll/approvals/hr',
              description: 'Review and advance payroll periods through the HR approval stage.',
              requiredPermission: 'HRM:PAYROLL_APPROVAL:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              ),
            },
            {
              name: 'Finance Approval',
              href: '/hrm/payroll/approvals/finance',
              description: 'Review and advance payroll periods through the Finance approval stage.',
              requiredPermission: 'HRM:PAYROLL_APPROVAL:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'CEO Approval',
              href: '/hrm/payroll/approvals/ceo',
              description: 'Review and advance payroll periods through the CEO approval stage.',
              requiredPermission: 'HRM:PAYROLL_APPROVAL:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z" />
                </svg>
              ),
            },

            { name: 'Reports', isHeader: true },
            // ── Payslip, Reports & Compliance ──
            {
              name: 'Payslip Generation',
              href: '/hrm/payroll/reports/payslips',
              description: 'Payslip distribution workspace. Compile finalized salary slip PDF registries, trigger secure digital delivery via email, and monitor real-time employee receipt status.',
              requiredPermission: 'HRM:PAYROLL_PAYSLIP:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Payroll History',
              href: '/hrm/payroll/reports/payroll-history',
              description: 'View complete payroll payment history per employee or per period. Track salary trends, compare monthly earnings, and review finalized payroll records.',
              requiredPermission: 'HRM:PAYROLL_HISTORY:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'Salary History',
              href: '/hrm/payroll/salary-history',
              description: 'Historical salary ledger logging increments, promotions, structure revisions, and payment variations across employee tenure.',
              requiredPermission: 'HRM:PAYROLL_SALARY_HISTORY:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Reports',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Employee Reports',
              href: '/hrm/reports/employee',
              requiredPermission: 'HRM:REPORT:SHOW',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
            {
              name: 'Attendance Reports',
              href: '/hrm/reports/attendance',
              requiredPermission: 'HRM:REPORT:SHOW',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
            },
            {
              name: 'Leave Reports',
              href: '/hrm/reports/leave',
              requiredPermission: 'HRM:REPORT:SHOW',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                </svg>
              ),
            },
            {
              name: 'Payroll Reports',
              href: '/hrm/reports/payroll',
              requiredPermission: 'HRM:PAYROLL_REPORT:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            }
          ]
        }
      ]
    },
    {
      name: 'Inventory',
      items: [
        {
          name: 'Inventory Dashboard',
          href: '/inventory/dashboard',
          requiredPermission: 'INVENTORY:DASHBOARD:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
        },
        {
          name: 'Categories',
          href: '/inventory/categories',
          requiredPermission: 'INVENTORY:CATEGORIES:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 7h.01M7 3h5c.512 0 1.024.195 1.414.586l7 7a2 2 0 010 2.828l-7 7a2 2 0 01-2.828 0l-7-7A1.994 1.994 0 013 12V7a4 4 0 014-4z" />
            </svg>
          ),
        },
        {
          name: 'Items',
          href: '/inventory/items',
          requiredPermission: 'INVENTORY:ITEMS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
          ),
        },
        {
          name: 'Rack Management',
          href: '/inventory/racks',
          requiredPermission: 'INVENTORY:STOCK:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 7v10c0 2 1.5 3 3.5 3h9c2 0 3.5-1 3.5-3V7M4 7c0-2 1.5-3 3.5-3h9c2 0 3.5 1 3.5 3M4 7h16M7 11h10M7 15h10" />
            </svg>
          ),
        },
        {
          name: 'Suppliers',
          href: '/inventory/suppliers',
          requiredPermission: 'INVENTORY:SUPPLIERS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h1a1 1 0 011 1v5m-4 0h4" />
            </svg>
          ),
        },
        {
          name: 'Return Types',
          href: '/inventory/return-types',
          requiredPermission: 'INVENTORY:RETURN_TYPES:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
            </svg>
          ),
        },
        {
          name: 'Procurement',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Purchase Requests',
              href: '/inventory/purchase-requests',
              requiredPermission: 'INVENTORY:PURCHASE_REQUESTS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              ),
            },
            {
              name: 'Purchase Orders',
              href: '/inventory/purchase-orders',
              requiredPermission: 'INVENTORY:PURCHASE_ORDERS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Goods Received Notes',
              href: '/inventory/grn',
              requiredPermission: 'INVENTORY:GRN:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4" />
                </svg>
              ),
            },
            {
              name: 'Supplier Return Notes',
              href: '/inventory/supplier-return-notes',
              requiredPermission: 'INVENTORY:SUPPLIER_RETURN_NOTES:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 15v-1a4 4 0 00-4-4H8m0 0l3 3m-3-3l3-3m9 14V5a2 2 0 00-2-2H6a2 2 0 00-2 2v16l4-2 4 2 4-2 4 2z" />
                </svg>
              ),
            },
            {
              name: 'Debit Notes',
              href: '/inventory/debit-notes',
              requiredPermission: 'INVENTORY:DEBIT_NOTES:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 14l6-6m-5.5.5h.01m4.99 5h.01M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16l3.5-2 3.5 2 3.5-2 3.5 2z" />
                </svg>
              ),
            },
            {
              name: 'Supplier Payments',
              href: '/inventory/supplier-payments',
              requiredPermission: 'INVENTORY:SUPPLIER_PAYMENTS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 9V7a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2m2 4h10a2 2 0 002-2v-6a2 2 0 00-2-2H9a2 2 0 00-2 2v6a2 2 0 002 2zm7-5a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Stock Operations',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Goods Transfer Requests',
              href: '/inventory/goods-transfer-requests',
              requiredPermission: 'INVENTORY:STOCK_TRANSFERS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                </svg>
              ),
            },
            {
              name: 'Goods Issue Notes',
              href: '/inventory/goods-issue-notes',
              requiredPermission: 'INVENTORY:GOODS_ISSUE_NOTES:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                </svg>
              ),
            },
            {
              name: 'Transfer In',
              href: '/inventory/transfer-in',
              requiredPermission: 'INVENTORY:TRANSFER_IN:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              ),
            },
            {
              name: 'Stock Management',
              href: '/inventory/stock',
              requiredPermission: 'INVENTORY:STOCK:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
            },
            {
              name: 'Inventory Overview',
              href: '/inventory/stock-overview',
              requiredPermission: 'INVENTORY:STOCK:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M3 14h18M3 6h18M3 18h18" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Consumption',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
            </svg>
          ),
          subItems: [
            {
              name: 'Material Request Notes',
              href: '/inventory/material-request-notes',
              requiredPermission: 'INVENTORY:MATERIAL_REQUEST_NOTES:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Inventory Consumption',
              href: '/inventory/inventory-consumption',
              requiredPermission: 'INVENTORY:INVENTORY_CONSUMPTION:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
              ),
            },
          ]
        },
        {
          name: 'Reports',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'Bincard Report',
              href: '/inventory/reports/bincard',
              requiredAnyPermission: ['INVENTORY:REPORTS:READ', 'INVENTORY:BINCARD:READ'],
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
              ),
            },
            {
              name: 'Stock Reports',
              isSubSubmenu: true,
              requiredAnyPermission: [
                'INVENTORY:REPORTS:READ',
                'INVENTORY:STOCK_LEVELS:READ',
                'INVENTORY:STOCK_MOVEMENTS:READ',
                'INVENTORY:STOCK_VALUATION:READ',
                'INVENTORY:STOCK_ANALYTICS:READ',
                'INVENTORY:STOCK_AGING:READ',
              ],
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
              ),
              subItems: [
                {
                  name: 'Stock Level Report',
                  href: '/inventory/reports/stock-levels',
                  requiredAnyPermission: ['INVENTORY:REPORTS:READ', 'INVENTORY:STOCK_LEVELS:READ'],
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                    </svg>
                  ),
                },
                {
                  name: 'Stock Movement Report',
                  href: '/inventory/reports/stock-movements',
                  requiredAnyPermission: ['INVENTORY:REPORTS:READ', 'INVENTORY:STOCK_MOVEMENTS:READ'],
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m4 6H4m0 0l4 4m-4-4l4-4" />
                    </svg>
                  ),
                },
                // {
                //   name: 'Analytics Report',
                //   href: '/inventory/reports/analytics',
                //   requiredAnyPermission: ['INVENTORY:REPORTS:READ', 'INVENTORY:STOCK_ANALYTICS:READ'],
                //   icon: (
                //     <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                //       <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                //     </svg>
                //   ),
                // },
                {
                  name: 'Stock Valuation Report',
                  href: '/inventory/reports/valuation',
                  requiredAnyPermission: ['INVENTORY:REPORTS:READ', 'INVENTORY:STOCK_VALUATION:READ'],
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 8h6m-5 4h4m-6 4h8M5 4h14a1 1 0 011 1v14a1 1 0 01-1 1H5a1 1 0 01-1-1V5a1 1 0 011-1z" />
                    </svg>
                  ),
                },
                {
                  name: 'Stock Aging Report',
                  href: '/inventory/reports/aging',
                  requiredPermission: 'INVENTORY:STOCK_AGING:READ',
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                },
              ]
            },
          ]
        },
        {
          name: 'Settings',
          isSubmenu: true,
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
          subItems: [
            {
              name: 'PO Approval Levels',
              href: '/inventory/settings/po-approval-levels',
              requiredPermission: 'INVENTORY:PO_APPROVAL_SETTINGS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              ),
            },
          ]
        },
      ]
    },

    {
      name: 'Administration',

      items: [
        {
          name: 'User Management',
          href: '/admin/users',
          requiredPermission: 'USERS:PROFILES:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4.354a4 4 0 110 5.292M15 21H3v-1a6 6 0 0112 0v1zm0 0h6v-1a6 6 0 00-9-5.197m13.5-9a2.5 2.5 0 11-5 0 2.5 2.5 0 015 0z" />
            </svg>
          ),
        },
        {
          name: 'Role Management',
          href: '/admin/roles',
          requiredPermission: 'RBAC:ROLES:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
            </svg>
          ),
        },
        {
          name: 'Permissions',
          href: '/admin/permissions',
          requiredPermission: 'RBAC:PERMISSIONS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>
          ),
        },
        {
          name: 'Locations',
          href: '/admin/locations',
          requiredPermission: 'ADMIN:LOCATIONS:READ',
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
          ),
        },
        {
          name: 'Report',
          isSubmenu: true,
          requiredPermission: ['ADMIN:REPORTS_USER_PERMISSIONS:READ', 'ADMIN:REPORTS_ACCESS_HISTORY:READ', 'ADMIN:REPORTS_AUDIT_LOGS:READ'],
          icon: (
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17v-2m3 2v-4m3 4v-6m2 10H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          ),
          subItems: [
            {
              name: 'User Permissions',
              href: '/admin/reports',
              requiredPermission: 'ADMIN:REPORTS_USER_PERMISSIONS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
              ),
            },
            {
              name: 'Access History',
              href: '/admin/reports/access-history',
              requiredPermission: 'ADMIN:REPORTS_ACCESS_HISTORY:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                </svg>
              ),
            },
            {
              name: 'Audit Logs',
              isSubSubmenu: true,
              requiredPermission: 'ADMIN:REPORTS_AUDIT_LOGS:READ',
              icon: (
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              ),
              subItems: [
                {
                  name: 'HRM',
                  href: '/admin/reports/audit-logs/hrm',
                  requiredPermission: 'ADMIN:REPORTS_AUDIT_LOGS:READ',
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                },
                {
                  name: 'Finance',
                  href: '/admin/reports/audit-logs/finance',
                  requiredPermission: 'ADMIN:REPORTS_AUDIT_LOGS:READ',
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                },
                {
                  name: 'Administration',
                  href: '/admin/reports/audit-logs/administration',
                  requiredPermission: 'ADMIN:REPORTS_AUDIT_LOGS:READ',
                  icon: (
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                  ),
                }
              ]
            },
          ],
        },
      ]
    }
  ];


  // Flatten navigation groups for compatibility
  const navigation = navigationGroups.flatMap(group => group.items);

  // Helper function to check if user is SUPER_ADMIN
  const isSuperAdmin = () => {
    if (!user) return false;

    // Check primary role
    const primaryRoleCode = typeof user.primaryRole === 'string'
      ? user.primaryRole
      : user.primaryRole?.roleCode;

    if (primaryRoleCode === 'SUPER_ADMIN' || user.role === 'SUPER_ADMIN') {
      return true;
    }

    // Check roles array
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles.some(role => {
        if (typeof role === 'string') return role === 'SUPER_ADMIN';
        if (typeof role === 'object' && role) {
          return role.roleCode === 'SUPER_ADMIN' || role.code === 'SUPER_ADMIN' || role.name === 'SUPER_ADMIN';
        }
        return false;
      });
    }

    return false;
  };

  const canViewTopDashboard = hasPermission(TOP_DASHBOARD_PERMISSION);

  let baseGroups = navigationGroups;
  if (hasStdEmployeeRole(user)) {
    baseGroups = getStdEmployeeNavigation(navigationGroups, { canViewTopDashboard });
  } else if (hasHodRole(user)) {
    baseGroups = getHodEmployeeNavigation(navigationGroups, { canViewTopDashboard });
  }

  const hasNavPermission = (navItem, parentItem) => {
    if (navItem.requiredAnyPermission?.length) {
      if (navItem.requiredAnyPermission.some((permission) => hasPermission(permission))) return true;
    } else if (!navItem.requiredPermission) {
      return true;
    } else if (hasPermission(navItem.requiredPermission)) {
      return true;
    }
    const strict = navItem.strictPermission ?? parentItem?.strictPermission;
    if (!strict && isAdmin()) return true;
    return false;
  };

  // Filter navigation groups based on permissions
  const filteredNavigationGroups = baseGroups.map(group => {
    const filteredItems = group.items.map(item => {
      // Special case: SUPER_ADMIN always has access to administration pages
      const adminPages = ['User Management', 'Role Management', 'Permissions', 'Locations', 'Report', 'User Permissions'];
      if (adminPages.includes(item.name) && isSuperAdmin()) {
        return item;
      }

      // Items that require a specific role (e.g. SUPER_ADMIN)
      if (item.requiredRole) {
        if (item.requiredRole === 'SUPER_ADMIN' && !isSuperAdmin()) return null;
      }

      // Check item permission
      if ((item.requiredPermission || item.requiredAnyPermission?.length) && !hasNavPermission(item)) {
        return null;
      }

      // If item has subItems, filter them too
      if (item.subItems) {
        const filteredSubItems = item.subItems.map(subItem => {
          // Skip header items (they are just labels, not clickable)
          if (subItem.isHeader) return subItem;
          // Check requiredRole restriction
          if (subItem.requiredRole) {
            if (subItem.requiredRole === 'SUPER_ADMIN' && !isSuperAdmin()) return null;
          }
          // Check requiredPermission restriction
          if ((subItem.requiredPermission || subItem.requiredAnyPermission?.length) && !hasNavPermission(subItem, item)) {
            return null;
          }
          // Handle nested sub-submenu
          if (subItem.isSubSubmenu && subItem.subItems) {
            const filteredSubSubItems = subItem.subItems.filter(subSubItem => {
              if (subSubItem.requiredRole) {
                if (subSubItem.requiredRole === 'SUPER_ADMIN' && !isSuperAdmin()) return false;
              }
              return !(subSubItem.requiredPermission || subSubItem.requiredAnyPermission?.length) || hasNavPermission(subSubItem, subItem) || hasNavPermission(subSubItem, item);
            });
            if (filteredSubSubItems.length === 0) return null;
            return {
              ...subItem,
              subItems: filteredSubSubItems
            };
          }
          return subItem;
        }).filter(subItem => subItem !== null);

        // Only show submenu if it has at least one visible non-header sub-item
        const visibleNonHeaderItems = filteredSubItems.filter(subItem => !subItem.isHeader);
        if (visibleNonHeaderItems.length === 0) {
          return null;
        }
        // Return a new item with filtered subItems
        return {
          ...item,
          subItems: filteredSubItems
        };
      }

      return item;
    }).filter(item => item !== null); // Remove null items

    return {
      ...group,
      items: filteredItems
    };
  }).filter(group => group.items.length > 0); // Remove groups with no items

  // Filter navigation based on permissions (legacy - keeping for compatibility)
  const filteredNavigation = navigation.filter(item => {
    if (item.hideFromNav) return false;
    if (item.requiredRole) {
      if (item.requiredRole === 'SUPER_ADMIN' && !isSuperAdmin()) return false;
    }
    return !item.requiredPermission || hasNavPermission(item);
  }).map(item => {
    // Filter sub-items based on permissions
    if (item.subItems) {
      return {
        ...item,
        subItems: item.subItems.map(subItem => {
          if (subItem.isHeader) return subItem;
          if (subItem.requiredRole) {
            if (subItem.requiredRole === 'SUPER_ADMIN' && !isSuperAdmin()) return null;
          }
          if ((subItem.requiredPermission || subItem.requiredAnyPermission?.length) && !hasNavPermission(subItem, item)) {
            return null;
          }
          if (subItem.isSubSubmenu && subItem.subItems) {
            const filteredSubSubItems = subItem.subItems.filter(subSubItem => {
              if (subSubItem.requiredRole) {
                if (subSubItem.requiredRole === 'SUPER_ADMIN' && !isSuperAdmin()) return false;
              }
              return !(subSubItem.requiredPermission || subSubItem.requiredAnyPermission?.length) || hasNavPermission(subSubItem, subItem) || hasNavPermission(subSubItem, item);
            });
            if (filteredSubSubItems.length === 0) return null;
            return {
              ...subItem,
              subItems: filteredSubSubItems
            };
          }
          return subItem;
        }).filter(subItem => subItem !== null)
      };
    }
    return item;
  });

  // Get current navigation breadcrumbs
  const getCurrentBreadcrumbs = () => {
    const breadcrumbs = [];

    // Items hidden from sidebar are still routable — resolve breadcrumbs from full config
    for (const group of navigationGroups) {
      for (const item of group.items) {
        if (!item?.hideFromNav || !item.href) continue;
        if (item.requiredRole) {
          if (item.requiredRole === 'SUPER_ADMIN' && !isSuperAdmin()) continue;
        }
        if (item.requiredPermission && !hasPermission(item.requiredPermission) && !isAdmin()) continue;
        if (location.pathname === item.href || location.pathname.startsWith(`${item.href}/`)) {
          breadcrumbs.push({ name: group.name, href: null, isGroup: true, description: item.description });
          breadcrumbs.push({ name: item.name, href: item.href, isGroup: false, description: item.description });
          return breadcrumbs;
        }
      }
    }

    // Search through all navigation groups and their items
    for (const group of filteredNavigationGroups) {
      for (const item of group.items) {
        // Check subItems first if they exist to prevent parent prefix matching collisions
        if (item.subItems) {
          for (const subItem of item.subItems) {
            if (subItem.isSubSubmenu && subItem.subItems) {
              for (const subSubItem of subItem.subItems) {
                const isSubSubActive = location.pathname === subSubItem.href || (
                  location.pathname.startsWith(subSubItem.href + '/') &&
                  !subItem.subItems.some(other => other !== subSubItem && other.href && other.href.startsWith(subSubItem.href + '/'))
                );
                if (isSubSubActive) {
                  breadcrumbs.push({ name: group.name, href: null, isGroup: true, description: subSubItem.description });
                  breadcrumbs.push({ name: item.name, href: null, isGroup: false, isParent: true, description: subSubItem.description });
                  breadcrumbs.push({ name: subItem.name, href: null, isGroup: false, isParent: true, description: subSubItem.description });
                  breadcrumbs.push({ name: subSubItem.name, href: subSubItem.href, isGroup: false, description: subSubItem.description });
                  return breadcrumbs;
                }
              }
            } else {
              // Precise active path matching for sub-route
              const isSubActive = location.pathname === subItem.href || (
                location.pathname.startsWith(subItem.href + '/') &&
                !item.subItems.some(other => other !== subItem && other.href && other.href.startsWith(subItem.href + '/'))
              );
              if (isSubActive) {
                breadcrumbs.push({ name: group.name, href: null, isGroup: true, description: subItem.description });
                breadcrumbs.push({ name: item.name, href: null, isGroup: false, isParent: true, description: subItem.description });
                breadcrumbs.push({ name: subItem.name, href: subItem.href, isGroup: false, description: subItem.description });
                return breadcrumbs;
              }
            }
          }
        }

        // Only check direct match if no subItems matched
        const isItemActive = location.pathname === item.href || (
          location.pathname.startsWith(item.href + '/') &&
          (!item.subItems || !item.subItems.some(subItem => subItem.href && subItem.href.startsWith(item.href + '/')))
        );

        if (isItemActive) {
          breadcrumbs.push({ name: group.name, href: null, isGroup: true, description: item.description });
          breadcrumbs.push({ name: item.name, href: item.href, isGroup: false, description: item.description });
          return breadcrumbs;
        }
      }
    }

    // Default breadcrumbs for dashboard or unknown routes
    return [{ name: 'Dashboard', href: '/dashboard', isGroup: false }];
  };

  // Helper function to get user role display
  const getUserRoleDisplay = () => {
    try {
      if (user?.primaryRole && typeof user.primaryRole === 'string') return user.primaryRole;
      if (user?.role && typeof user.role === 'string') return user.role;
      if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
        const firstRole = user.roles[0];
        if (typeof firstRole === 'string') return firstRole;
        if (firstRole && typeof firstRole === 'object') {
          // Handle role objects with different possible properties
          return firstRole.roleCode || firstRole.code || firstRole.name || firstRole.role || 'USER';
        }
      }
      return 'USER';
    } catch (error) {
      console.error('Error getting user role display:', error);
      return 'USER';
    }
  };

  // Helper function to get user role color
  const getUserRoleColor = () => {
    try {
      const role = getUserRoleDisplay();
      if (typeof role !== 'string') return 'bg-green-100 text-green-800';

      switch (role.toUpperCase()) {
        case 'SUPER_ADMIN':
        case 'SUPERADMIN':
          return 'bg-red-100 text-red-800';
        case 'ADMIN':
          return 'bg-orange-100 text-orange-800';
        case 'MANAGER':
          return 'bg-yellow-100 text-yellow-800';
        case 'USER':
        case 'EMPLOYEE':
        default:
          return 'bg-green-100 text-green-800';
      }
    } catch (error) {
      console.error('Error getting user role color:', error);
      return 'bg-green-100 text-green-800';
    }
  };

  return (
    <ErrorBoundary>
      <div className="h-screen flex overflow-hidden bg-gray-50">
        {/* Sidebar */}
        <div className={`${sidebarOpen ? 'translate-x-0' : '-translate-x-full'
          } fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-xl border-r border-gray-200 transform transition-all duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 flex flex-col`}>
          <div className="flex items-center justify-center h-12 px-3 bg-gradient-to-r from-indigo-600 to-purple-600 flex-shrink-0">
            <div className="flex items-center">
              <img
                src={getSlimLogoPath()}
                alt="KD Rise Motor"
                className="h-10 w-auto"
                onError={(e) => {
                  e.target.style.display = 'none';
                  e.target.nextSibling.style.display = 'inline-block';
                }}
              />
            </div>
          </div>

          <nav ref={navScrollRef} className="flex-1 px-2 py-2 space-y-0.5 overflow-y-auto min-h-0">
            {filteredNavigationGroups.map((group, groupIdx) => (
              <div key={`${group.name}-${groupIdx}`} className="mb-1.5">
                {/* Group Header */}
                {group.name !== 'Overview' ? (
                  <button
                    onClick={() => toggleGroup(group.name)}
                    className="w-full px-2 mb-1 flex items-center justify-between hover:bg-gray-50 rounded py-0.5 transition-colors"
                  >
                    <h3 className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
                      {group.name}
                    </h3>
                    <svg
                      className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${expandedGroups.has(group.name) ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                ) : null}

                {/* Group Items - Collapsible (Overview is always shown) */}
                {(expandedGroups.has(group.name) || group.name === 'Overview') && (
                  <div className="space-y-0.5">
                    {group.items.map((item, itemIdx) => (
                      <div key={`${item.name}-${itemIdx}`}>
                        {item.isHeader ? (
                          <div className="px-2 py-1 mt-2 text-[10px] font-bold text-gray-400 uppercase tracking-tighter bg-gray-50/50 rounded-sm">
                            {item.name}
                          </div>
                        ) : item.isSubmenu ? (
                          // Submenu item
                          <div>
                            <button
                              onClick={() => toggleSubmenu(item.name)}
                              className={`w-full flex items-center justify-between px-2 py-1.5 text-xs font-medium rounded transition-all duration-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900`}
                            >
                              <div className="flex items-center flex-1 min-w-0">
                                <span className="mr-2 flex-shrink-0 text-gray-500">
                                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    {item.icon.props.children}
                                  </svg>
                                </span>
                                <span className="truncate text-xs">{item.name}</span>
                              </div>
                              <div className="flex items-center flex-shrink-0 ml-2">
                                {item.badge > 0 && (
                                  <span className="mr-1.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full bg-red-100 text-red-600">
                                    {item.badge}
                                  </span>
                                )}
                                <svg
                                  className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${expandedSubmenus.has(item.name) ? 'rotate-180' : ''
                                    }`}
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                              </div>
                            </button>
                            {/* Submenu items */}
                            {expandedSubmenus.has(item.name) && (
                              <div className="ml-5 mt-1 space-y-0.5 border-l border-gray-100 pl-1">
                                {item.subItems.map((subItem, subIdx) => {
                                  if (subItem.isHeader) {
                                    return (
                                      <div key={subIdx} className="px-2 py-1 mt-2 text-[10px] font-bold text-gray-400 uppercase tracking-tighter bg-gray-50/50 rounded-sm">
                                        {subItem.name}
                                      </div>
                                    );
                                  }

                                  if (subItem.isSubSubmenu) {
                                    const isSubSubmenuExpanded = expandedSubmenus.has(subItem.name);
                                    return (
                                      <div key={`${subItem.name}-${subIdx}`}>
                                        <button
                                          onClick={() => toggleSubmenu(subItem.name)}
                                          className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-medium rounded transition-all duration-200 text-gray-700 hover:bg-gray-100 hover:text-gray-900"
                                        >
                                          <div className="flex items-center flex-1 min-w-0">
                                            <span className="mr-2 flex-shrink-0 text-gray-500">
                                              <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                {subItem.icon.props.children}
                                              </svg>
                                            </span>
                                            <span className="truncate text-xs">{subItem.name}</span>
                                          </div>
                                          <div className="flex items-center flex-shrink-0 ml-2">
                                            <svg
                                              className={`w-3 h-3 text-gray-400 transition-transform duration-200 ${isSubSubmenuExpanded ? 'rotate-180' : ''}`}
                                              fill="none"
                                              viewBox="0 0 24 24"
                                              stroke="currentColor"
                                            >
                                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                            </svg>
                                          </div>
                                        </button>
                                        {isSubSubmenuExpanded && (
                                          <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-100 pl-1">
                                            {subItem.subItems.map((subSubItem, subSubIdx) => {
                                              const isSubSubActive = location.pathname === subSubItem.href || (
                                                location.pathname.startsWith(subSubItem.href + '/') &&
                                                !subItem.subItems.some(other => other !== subSubItem && other.href && other.href.startsWith(subSubItem.href + '/'))
                                              );

                                              return (
                                                <Link
                                                  key={`${subSubItem.name}-${subSubIdx}`}
                                                  to={subSubItem.href}
                                                  data-nav-active={isSubSubActive ? 'true' : undefined}
                                                  className={`${isSubSubActive
                                                    ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                                                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                                    } group flex items-center px-2 py-1 text-[11px] font-medium rounded transition-all duration-200`}
                                                >
                                                  <span className={`mr-2 flex-shrink-0 ${isSubSubActive ? 'text-white' : 'text-gray-400'}`}>
                                                    <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                      {subSubItem.icon.props.children}
                                                    </svg>
                                                  </span>
                                                  <span className="truncate flex-1 font-medium">{subSubItem.name}</span>
                                                </Link>
                                              );
                                            })}
                                          </div>
                                        )}
                                      </div>
                                    );
                                  }

                                  const isSubActive = location.pathname === subItem.href || (
                                    location.pathname.startsWith(subItem.href + '/') &&
                                    !item.subItems.some(other => other !== subItem && other.href && other.href.startsWith(subItem.href + '/'))
                                  );

                                  return (
                                    <Link
                                      key={`${subItem.name}-${subIdx}`}
                                      to={subItem.href}
                                      data-nav-active={isSubActive ? 'true' : undefined}
                                      className={`${isSubActive
                                        ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                                        : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                                        } group flex items-center px-2 py-1.5 text-xs font-medium rounded transition-all duration-200`}
                                    >
                                      <span className={`mr-2 flex-shrink-0 ${isSubActive
                                        ? 'text-white'
                                        : 'text-gray-400'
                                        }`}>
                                        <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                          {subItem.icon.props.children}
                                        </svg>
                                      </span>
                                      <span className="truncate flex-1 text-[11px] font-medium">{subItem.name}</span>
                                      {subItem.badge > 0 && (
                                        <span className={`ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-bold rounded-full ${isSubActive ? 'bg-white/20 text-white' : 'bg-red-100 text-red-600'}`}>
                                          {subItem.badge}
                                        </span>
                                      )}
                                    </Link>
                                  );
                                })}
                              </div>
                            )}
                          </div>
                        ) : (
                          // Regular item
                          <Link
                            to={item.href}
                            data-nav-active={(location.pathname === item.href || location.pathname.startsWith(item.href + '/')) ? 'true' : undefined}
                            className={`${location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                              ? 'bg-gradient-to-r from-indigo-500 to-purple-600 text-white shadow-md'
                              : 'text-gray-700 hover:bg-gray-100 hover:text-gray-900'
                              } group flex items-center px-2 py-1.5 text-xs font-medium rounded transition-all duration-200`}
                          >
                            <span className={`mr-2 flex-shrink-0 ${location.pathname === item.href || location.pathname.startsWith(item.href + '/')
                              ? 'text-white'
                              : 'text-gray-500'
                              }`}>
                              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                {item.icon.props.children}
                              </svg>
                            </span>
                            <span className="truncate text-xs">{item.name}</span>
                          </Link>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </nav>
        </div>

        {/* Main content */}
        <div className="flex flex-col w-0 flex-1 overflow-hidden">
          {/* Top bar */}
          <div className="relative z-40 flex-shrink-0 flex h-12 bg-white shadow-sm border-b border-gray-200">
            <button
              className="px-3 text-gray-500 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-indigo-500 lg:hidden transition-colors"
              onClick={() => setSidebarOpen(!sidebarOpen)}
            >
              <span className="sr-only">Open sidebar</span>
              <svg
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h7"
                />
              </svg>
            </button>
            <div className="flex-1 px-4 flex justify-between items-center">
              <div className="flex items-center min-w-0 flex-1">
                {(() => {
                  const breadcrumbs = getCurrentBreadcrumbs();
                  const currentBreadcrumb = breadcrumbs[breadcrumbs.length - 1];

                  return (
                    <>
                      {/* Full breadcrumbs for larger screens */}
                      <div className="hidden md:flex items-center min-w-0">
                        <nav className="flex items-center space-x-1 text-sm min-w-0">
                          {breadcrumbs.map((breadcrumb, index, array) => (
                            <div key={index} className="flex items-center min-w-0">
                              {breadcrumb.href && !breadcrumb.isGroup ? (
                                <Link
                                  to={breadcrumb.href}
                                  className={`${index === array.length - 1
                                    ? 'text-gray-900 font-semibold'
                                    : 'text-gray-500 hover:text-gray-700'
                                    } transition-colors truncate max-w-xs`}
                                  title={breadcrumb.name}
                                >
                                  {breadcrumb.name}
                                </Link>
                              ) : (
                                <span
                                  className={`${breadcrumb.isGroup
                                    ? 'text-indigo-600 font-medium'
                                    : breadcrumb.isParent
                                      ? 'text-gray-600'
                                      : index === array.length - 1
                                        ? 'text-gray-900 font-semibold'
                                        : 'text-gray-500'
                                    } truncate max-w-xs`}
                                  title={breadcrumb.name}
                                >
                                  {breadcrumb.name}
                                </span>
                              )}
                              {index < array.length - 1 && (
                                <svg
                                  className="w-3 h-3 mx-1.5 text-gray-400 flex-shrink-0"
                                  fill="none"
                                  viewBox="0 0 24 24"
                                  stroke="currentColor"
                                >
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M9 5l7 7-7 7"
                                  />
                                </svg>
                              )}
                            </div>
                          ))}
                        </nav>
                        {currentBreadcrumb?.description && (
                          <div className="relative ml-2" data-breadcrumb-help>
                            <button
                              type="button"
                              aria-label={`${currentBreadcrumb.name} description`}
                              title={currentBreadcrumb.description}
                              onClick={() => setOpenBreadcrumbHelp(prev => !prev)}
                              className="group flex h-6 w-6 items-center justify-center rounded-full text-amber-500 transition-colors hover:bg-amber-50 hover:text-amber-600"
                            >
                              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3a7 7 0 017 7c0 2.333-1.01 3.873-2.43 5.078-.965.817-1.57 1.472-1.57 2.422V18a1 1 0 01-1 1h-2a1 1 0 01-1-1v-.5c0-.95-.605-1.605-1.57-2.422C7.01 13.873 6 12.333 6 10a7 7 0 015-7zm1 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                              </svg>
                            </button>
                            <div className={`absolute left-0 top-full z-50 mt-2 w-80 rounded-lg border border-amber-100 bg-white p-3 text-xs leading-5 text-gray-600 shadow-xl transition-all duration-150 ${openBreadcrumbHelp ? 'visible opacity-100' : 'invisible opacity-0 group-hover:visible group-hover:opacity-100'}`}>
                              <div className="mb-1 font-semibold text-gray-900">{currentBreadcrumb.name}</div>
                              <p>{currentBreadcrumb.description}</p>
                            </div>
                          </div>
                        )}
                      </div>

                      {/* Simplified breadcrumb for mobile */}
                      <div className="md:hidden flex items-center space-x-2">
                        {(() => {
                          const current = breadcrumbs[breadcrumbs.length - 1];
                          const parent = breadcrumbs.length > 1 ? breadcrumbs[breadcrumbs.length - 2] : null;

                          return (
                            <>
                              {parent && (
                                <>
                                  <span className="text-gray-500 text-sm truncate max-w-[100px]" title={parent.name}>
                                    {parent.name}
                                  </span>
                                  <svg
                                    className="w-3 h-3 text-gray-400 flex-shrink-0"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M9 5l7 7-7 7"
                                    />
                                  </svg>
                                </>
                              )}
                              <span className="text-gray-900 font-semibold text-sm truncate max-w-[120px]" title={current.name}>
                                {current.name}
                              </span>
                              {current?.description && (
                                <div className="relative" data-breadcrumb-help>
                                  <button
                                    type="button"
                                    aria-label={`${current.name} description`}
                                    title={current.description}
                                    onClick={() => setOpenBreadcrumbHelp(prev => !prev)}
                                    className="flex h-6 w-6 items-center justify-center rounded-full text-amber-500 transition-colors hover:bg-amber-50 hover:text-amber-600"
                                  >
                                    <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 3a7 7 0 017 7c0 2.333-1.01 3.873-2.43 5.078-.965.817-1.57 1.472-1.57 2.422V18a1 1 0 01-1 1h-2a1 1 0 01-1-1v-.5c0-.95-.605-1.605-1.57-2.422C7.01 13.873 6 12.333 6 10a7 7 0 015-7zm1 18a1.5 1.5 0 100-3 1.5 1.5 0 000 3z" />
                                    </svg>
                                  </button>
                                  <div className={`absolute left-0 top-full z-50 mt-2 w-72 max-w-[80vw] rounded-lg border border-amber-100 bg-white p-3 text-xs leading-5 text-gray-600 shadow-xl transition-all duration-150 ${openBreadcrumbHelp ? 'visible opacity-100' : 'invisible opacity-0'}`}>
                                    <div className="mb-1 font-semibold text-gray-900">{current.name}</div>
                                    <p>{current.description}</p>
                                  </div>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </div>
                    </>
                  );
                })()}
              </div>
              <div className="flex items-center space-x-4">
                {/* Location Switcher */}
                <LocationSwitcher />
                <NotificationBell />
                {/* User Dropdown */}
                <div className="relative" ref={dropdownRef}>
                  <button
                    onClick={() => setDropdownOpen(!dropdownOpen)}
                    className="flex items-center space-x-2 p-1.5 rounded-lg hover:bg-gray-50 transition-colors focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <div className="hidden md:block text-right">
                      <div className="text-sm font-medium text-gray-900">{user?.profile?.displayName || user?.name || 'User'}</div>
                      <div className="text-xs text-gray-500">{user?.email || 'No email'}</div>
                    </div>
                    <div className="w-7 h-7 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-full flex items-center justify-center overflow-hidden border border-gray-200/50">
                      <SecureImage
                        src={user?.profile?.pendingProfilePhoto || user?.profile?.profilePhoto}
                        alt="User Profile"
                        className="w-full h-full object-cover"
                        fallback={
                          <span className="text-white font-semibold text-[10px]">
                            {((user?.profile?.displayName || user?.name || 'U').charAt(0)?.toUpperCase())}
                          </span>
                        }
                      />
                    </div>
                    <svg
                      className={`w-4 h-4 text-gray-500 transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''
                        }`}
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>

                  {/* Dropdown Menu */}
                  {dropdownOpen && (
                    <div className="absolute right-0 mt-1 w-52 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {/* User Info */}
                      <div className="px-3 py-2 border-b border-gray-100">
                        <div className="text-sm font-medium text-gray-900">{user?.profile?.displayName || user?.name || 'User'}</div>
                        <div className="text-xs text-gray-500">{user?.email || 'No email'}</div>
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium mt-2 ${getUserRoleColor()
                          }`}>
                          {String(getUserRoleDisplay())}
                        </span>
                      </div>

                      {/* Profile Link */}
                      <Link
                        to="/profile"
                        className="flex items-center px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                        </svg>
                        Profile Settings
                      </Link>

                      {/* Logout Button */}
                      <button
                        onClick={handleLogout}
                        className="flex items-center w-full px-3 py-1.5 text-sm text-red-700 hover:bg-red-50 transition-colors"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                        </svg>
                        Sign Out
                      </button>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Page content */}
          <main className="flex-1 relative overflow-y-auto focus:outline-none bg-gray-50">
            <div>
              <div className="max-w-full mx-auto">
                {children}
              </div>
            </div>
          </main>
        </div>

        {/* Mobile sidebar overlay */}
        {sidebarOpen && (
          <div
            className="fixed inset-0 z-40 lg:hidden"
            onClick={() => setSidebarOpen(false)}
          >
            <div className="absolute inset-0 backdrop-blur-sm transition-opacity"></div>
          </div>
        )}
      </div>
    </ErrorBoundary>
  );
};

export default MainLayout;
